import { User as PersonIcon } from '@clearkit/icons';
import { CKAvatar, CKDrawer, CKTab, CKTabList, CKTabs } from 'clearkit';
import { useRef } from 'react';
import Helmet from 'react-helmet';

import AttributeBrowser from '~/components/AttributeBrowser';
import LoadingArea from '~/components/LoadingArea';
import PersonDetails from '~/pages/people/Show/Details';

import { usePersonLookup } from './hooks';
import MissingRecord from './MissingRecord';

type PersonLookupProps = {
  email: string;
};

const LookupSidebarPerson = ({ email }: PersonLookupProps) => {
  const overflowScrollRef = useRef<HTMLDivElement>(null);

  const handleTabChange = (stickyHeaderBounds: any) => {
    const stickyHeaderGap = 28;
    const customOffset =
      stickyHeaderBounds.top - stickyHeaderBounds.height - stickyHeaderGap;
    if (overflowScrollRef.current) {
      overflowScrollRef.current.scrollTo({ top: customOffset });
    }
  };

  const { data, isLoading, isError, refetch } = usePersonLookup(email);

  const person = data?.data;

  if (isLoading) return <LoadingArea className="x-sidebar" loading />;
  if (isError)
    return <MissingRecord onClickRefetch={() => refetch()} type="person" />;
  if (!person) return <div className="x-sidebar">Default State</div>;

  return (
    <div className="x-sidebar">
      <Helmet title={person.name} />
      <CKDrawer.Header className="border-none">
        <div className="flex items-center flex-1">
          <div className="mr-4">
            <CKAvatar className="shadow-sm" size={56} src={person.avatar} />
          </div>

          <div>
            <CKDrawer.Heading>{person.name || 'Loading...'}</CKDrawer.Heading>
            {person.email ? (
              <a
                className="text-blue"
                href={`mailto://${person.email}`}
                rel="noopener noreferrer"
                target="_blank"
              >
                {person.email}
              </a>
            ) : (
              <span>&mdash;</span>
            )}
          </div>
        </div>
      </CKDrawer.Header>

      <CKTabs
        aria-label="Person tabs"
        className="flex flex-col flex-grow min-h-0"
        index={0}
        isBordered
      >
        <CKTabList
          aria-label="Person tabs"
          className="flex-shrink-0 px-10 -mt-4"
        >
          <CKTab icon={<PersonIcon />} label="Person details">
            Person details
          </CKTab>
        </CKTabList>

        <div className="overflow-y-auto" ref={overflowScrollRef}>
          <PersonDetails person={person} />

          <LoadingArea loading={!person?.enrichment}>
            <AttributeBrowser
              enrichment={person.enrichment}
              object={person}
              onTabChange={handleTabChange}
              traits={{}}
            />
          </LoadingArea>
        </div>
      </CKTabs>
    </div>
  );
};

export { LookupSidebarPerson };
